import React, { useState } from 'react';
import { ChevronDown, Phone, Mail, HelpCircle } from 'lucide-react';

const Faq = () => {
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (category, index) => {
    const key = `${category}-${index}`;
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const faqData = {
    payment: {
      title: 'Payment',
      icon: '💳',
      color: 'from-blue-500 to-blue-600',
      questions: [
        {
          question: 'How do I pay?',
          answer: 'You can make payment through multiple convenient methods at Vegatas Bike Rental Service. We accept online payments via UPI, credit cards, debit cards, and net banking. You can also pay in cash at our office during pickup. For advance bookings, we require a partial payment to confirm your reservation, with the balance due at the time of vehicle pickup.'
        },
        {
          question: 'Is fuel included in the tariff?',
          answer: 'No, fuel is not included in the amount you pay for two-wheeler rental. The vehicle will be provided with a basic fuel level, and you are responsible for refueling during your rental period. Upon return, the vehicle should have the same fuel level as at pickup, or fuel charges will be deducted from your security deposit.'
        },
        {
          question: 'What methods of payment does Vegatas Bike accept?',
          answer: 'Vegatas Bike accepts various payment methods including UPI (Google Pay, PhonePe, Paytm), credit cards (Visa, Mastercard, American Express), debit cards, net banking, and cash payments at our office. For online bookings, we recommend using digital payment methods for instant confirmation. All online transactions are secured with SSL encryption for your safety.'
        },
        {
          question: 'What documents are required to book a bike?',
          answer: 'To book a bike with Vegatas, you need to provide: 1) A valid government-issued photo ID (Aadhaar Card, Passport, or Voter ID), 2) A valid original driving license with two-wheeler endorsement, 3) One passport-size photograph, and 4) Proof of current address (if different from ID). All documents must be original and valid. For tourists, a valid passport and international driving permit are required.'
        },
        {
          question: 'Can I extend my booking? What are the charges for the same?',
          answer: 'Yes, you can extend your booking subject to vehicle availability. To extend, contact us at least 2-3 hours before your scheduled return time at 9730736858 or email Vegatasbike00@gmail.com. Extension charges are calculated at the standard daily, weekly, or monthly rate depending on your extension period. If you extend without prior notification, additional charges may apply. Early notification helps us serve you better.'
        },
        {
          question: 'Can I reschedule my booking?',
          answer: 'Yes, you can reschedule your booking based on vehicle availability. Rescheduling is free if done 24 hours or more before your pickup time. If you reschedule within 24 hours of pickup, a nominal rescheduling fee of 10% of the booking amount may apply. To reschedule, contact our support team at 9730736858 or email Vegatasbike00@gmail.com with your booking details and preferred new dates.'
        },
        {
          question: 'How to cancel my booking? What is the cancellation policy?',
          answer: 'To cancel your booking, email us at Vegatasbike00@gmail.com or call 9730736858 with your booking reference number. Our cancellation policy: 72+ hours before pickup - 90% refund (10% processing fee), 24-72 hours before pickup - 50% refund, 6-24 hours before pickup - 25% refund, Less than 6 hours or no-show - No refund. Security deposit (if paid) will always be refunded. Refunds are processed within 5-7 business days to the original payment method.'
        },
        {
          question: 'Can I extend my trip after it ends?',
          answer: 'If you need to extend beyond your original return time, contact us immediately at 9730736858. Extensions are subject to vehicle availability and booking schedule. If approved, regular extension rates apply. For extensions requested after the scheduled end time without prior approval, late return charges of ₹200 for the first 30 minutes apply, followed by standard hourly rates. We have a 30-minute grace period for returns.'
        }
      ]
    },
    damage: {
      title: 'Damage & Breakdowns',
      icon: '🔧',
      color: 'from-orange-500 to-orange-600',
      questions: [
        {
          question: 'What happens if the bike breaks down during my rental?',
          answer: 'If the vehicle breaks down due to mechanical failure (not caused by misuse), immediately call our 24/7 support at 9730736858. We will arrange for roadside assistance or provide a replacement vehicle at no additional cost. Do not attempt repairs yourself. Keep the vehicle in a safe location and share your location with our team. We aim to resolve breakdown issues within 2-3 hours in Pune city limits.'
        },
        {
          question: 'Am I responsible for damages to the bike?',
          answer: 'Yes, you are responsible for any damage to the vehicle during your rental period. This includes scratches, dents, mechanical damage, or missing parts. The repair cost will be assessed and deducted from your security deposit. Normal wear and tear is acceptable. We recommend inspecting the vehicle thoroughly during pickup and reporting any existing damage. Consider our damage waiver insurance for added protection.'
        },
        {
          question: 'What should I do in case of an accident?',
          answer: 'In case of an accident: 1) Ensure everyone\'s safety first and call emergency services if needed, 2) Contact Vegatas immediately at 9730736858, 3) File a police report (FIR) if required, 4) Take photos of the accident scene and damage, 5) Exchange information with other parties involved, 6) Do not leave the scene without proper documentation. Our insurance covers third-party liability, but you may be responsible for deductibles. Never admit fault at the scene.'
        },
        {
          question: 'Is insurance included in the rental?',
          answer: 'Yes, all our vehicles come with basic insurance that covers third-party liability as per Indian Motor Vehicle Act. However, this does not cover damage to the rented vehicle or theft. We offer optional comprehensive damage waiver insurance at an additional cost of ₹50-100 per day, which significantly reduces your liability in case of damage or theft. We highly recommend opting for this additional coverage for peace of mind.'
        },
        {
          question: 'What if the bike is stolen during my rental period?',
          answer: 'If the vehicle is stolen, immediately: 1) File an FIR (police complaint) at the nearest police station, 2) Inform Vegatas within 2 hours at 9730736858, 3) Provide us with the FIR copy and all relevant documentation. Without proper police documentation, you will be liable for the full vehicle value. With valid FIR and if you have damage waiver insurance, your liability is limited to the insurance deductible amount. Always park in secure locations and use the provided locks.'
        }
      ]
    },
    general: {
      title: 'General Information',
      icon: 'ℹ️',
      color: 'from-green-500 to-green-600',
      questions: [
        {
          question: 'What is the minimum age requirement to rent a bike?',
          answer: 'You must be at least 18 years old to rent a two-wheeler from Vegatas Bike Rental Service. You must also hold a valid driving license with two-wheeler endorsement that has been held for a minimum of 1 year. These requirements are mandated by Indian traffic laws and our insurance policy. Age proof will be verified through your government-issued photo ID during the booking process.'
        },
        {
          question: 'What types of bikes are available for rent?',
          answer: 'Vegatas offers a diverse fleet of well-maintained two-wheelers including: Economy scooters (Honda Activa, TVS Jupiter) ideal for city rides, Standard motorcycles (Bajaj Pulsar, Hero Splendor) for daily commuting, Premium bikes (Royal Enfield, KTM) for longer trips and adventures. All vehicles are regularly serviced, sanitized, and undergo safety checks. Visit our website or call 9730736858 for current availability and specific models.'
        },
        {
          question: 'Do you provide helmets and other safety gear?',
          answer: 'Yes, we provide complimentary helmets (2 per vehicle) that are sanitized after each use. Additional safety gear including riding gloves, knee guards, and reflective jackets are available on request at no extra cost (subject to availability). We strongly encourage wearing helmets at all times as it is mandatory by law and ensures your safety. If you prefer using your own helmet for hygiene reasons, you are welcome to do so.'
        },
        {
          question: 'What are your operating hours?',
          answer: 'Vegatas Bike Rental Service operates Monday through Sunday from 9:00 AM to 8:00 PM at our Pune office. Vehicle pickups and returns can be scheduled during these hours. For customers requiring early morning pickup or late evening return, please contact us in advance at 9730736858, and we will do our best to accommodate your schedule. Our 24/7 customer support helpline is always available for breakdowns and emergencies.'
        },
        {
          question: 'Can I take the bike outside Pune?',
          answer: 'Yes, you can take the vehicle outside Pune city limits, but please inform us in advance about your intended route and destinations. For trips beyond 200 km from Pune or interstate travel, additional charges and permissions may apply. Some restricted areas may require special authorization. Ensure you have all vehicle documents with you during intercity travel. The vehicle must be returned to our Pune office unless prior arrangements are made.'
        },
        {
          question: 'Is there a mileage limit on rentals?',
          answer: 'Our rental packages include generous mileage limits: Daily rentals include 100 km/day, Weekly rentals include 700 km/week, and Monthly rentals include 3000 km/month. If you exceed these limits, additional charges of ₹3-5 per km apply depending on the vehicle type. For customers planning longer trips, we offer unlimited mileage packages at a slightly higher rate. Odometer readings are recorded at pickup and return to calculate usage.'
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center mb-6">
            <HelpCircle className="w-12 h-12 mr-4" />
            <h1 className="text-4xl md:text-5xl font-bold">
              Frequently Asked Questions
            </h1>
          </div>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Find answers to common questions about Vegatas Bike Rental Service
          </p>
        </div>
      </div>

      {/* FAQ Categories */}
      <div className="max-w-5xl mx-auto px-4 py-12">
        {Object.entries(faqData).map(([key, category]) => (
          <div key={key} className="mb-12">
            {/* Category Header */}
            <div className={`bg-gradient-to-r ${category.color} text-white rounded-xl p-6 mb-6 shadow-md`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <span className="text-4xl">{category.icon}</span>
                  <div>
                    <h2 className="text-2xl font-bold">{category.title}</h2>
                    <p className="text-sm text-white/80 mt-1">
                      {category.questions.length} questions
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Questions */}
            <div className="space-y-4">
              {category.questions.map((item, index) => {
                const itemKey = `${key}-${index}`;
                const isOpen = openItems[itemKey];

                return (
                  <div
                    key={index}
                    className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-300"
                  >
                    <button
                      onClick={() => toggleItem(key, index)}
                      className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                    >
                      <span className="font-semibold text-gray-900 pr-8 text-lg">
                        {item.question}
                      </span>
                      <ChevronDown
                        className={`w-6 h-6 text-gray-500 flex-shrink-0 transition-transform duration-300 ${
                          isOpen ? 'transform rotate-180' : ''
                        }`}
                      />
                    </button>
                    <div
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${
                        isOpen ? 'max-h-96' : 'max-h-0'
                      }`}
                    >
                      <div className="px-6 pb-5 text-gray-700 leading-relaxed border-t border-gray-100 pt-4">
                        {item.answer}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Contact Support Section */}
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 py-16 px-4 mt-12">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <HelpCircle className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Still have questions?
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Can't find what you're looking for? Our support team is here to help! Reach out to us and we'll respond as soon as possible.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:9730736858"
                className="inline-flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-4 rounded-lg transition-colors duration-300 shadow-md hover:shadow-lg"
              >
                <Phone className="w-5 h-5" />
                <span>Call: 9730736858</span>
              </a>
              <a
                href="mailto:Vegatasbike00@gmail.com"
                className="inline-flex items-center justify-center space-x-2 bg-white hover:bg-gray-50 text-blue-600 font-semibold px-8 py-4 rounded-lg border-2 border-blue-600 transition-colors duration-300"
              >
                <Mail className="w-5 h-5" />
                <span>Email Support</span>
              </a>
            </div>
            <div className="mt-8 pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                Support Hours: Monday - Sunday, 9:00 AM - 8:00 PM
                <br />
                24/7 Emergency Roadside Assistance Available
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl shadow-md p-6 text-center border border-gray-100 hover:shadow-lg transition-shadow duration-300">
            <div className="text-3xl mb-3">📍</div>
            <h3 className="font-semibold text-gray-900 mb-2">Visit Us</h3>
            <p className="text-sm text-gray-600">
              Pune-Nashik Highway, Chakan, Pune 410501
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6 text-center border border-gray-100 hover:shadow-lg transition-shadow duration-300">
            <div className="text-3xl mb-3">🌐</div>
            <h3 className="font-semibold text-gray-900 mb-2">Website</h3>
            <a
              href="https://www.Vegatasbike.in"
              className="text-sm text-blue-600 hover:text-blue-700"
              target="_blank"
              rel="noopener noreferrer"
            >
              www.Vegatasbike.in
            </a>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6 text-center border border-gray-100 hover:shadow-lg transition-shadow duration-300">
            <div className="text-3xl mb-3">💬</div>
            <h3 className="font-semibold text-gray-900 mb-2">Live Chat</h3>
            <p className="text-sm text-gray-600">
              Chat with our support team online
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Faq;
